import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { log } from "../lib/log";

const WalletContext = createContext(null);

// Which transferable currencies move to which airline program (matches TRANSFER_PARTNERS keys in mock.js)
export const CARD_PARTNERS = {
  AMEX: ["DL", "BA", "AF", "AC", "VS", "SQ", "EK", "QR", "EI", "AV", "NH", "QF"],
  CHASE: ["UA", "BA", "AF", "AC", "VS", "SQ", "EK", "EI"],
  CITI: ["AF", "SQ", "EK", "QR", "TK", "AV", "EI", "VS"],
  CAPITALONE: ["AF", "AC", "BA", "SQ", "EK", "QR", "TK", "AV", "EI", "VS"],
  BILT: ["AA", "UA", "AF", "AC", "VS", "EK", "TK", "AV", "EI"],
};

export function WalletProvider({ children }) {
  const { user } = useAuth();
  const [balances, setBalances] = useState({});

  const refresh = useCallback(async () => {
    if (!user?.pro) { setBalances({}); return; }
    try { setBalances((await api.getWallet()).balances || {}); } catch (err) { log.warn("Wallet load failed", err?.response?.status); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (next) => {
    const res = await api.saveWallet(next);
    setBalances(res.balances);
    return res.balances;
  }, []);

  // Points available for a deal = direct program balance + every card currency that transfers to it
  const affordability = useCallback((deal) => {
    if (!user?.pro || Object.keys(balances).length === 0) return null;
    const code = deal.airlineCode;
    const sources = [];
    if (balances[code]) sources.push({ key: code, amount: balances[code] });
    Object.entries(CARD_PARTNERS).forEach(([card, programs]) => { if (balances[card] && programs.includes(code)) sources.push({ key: card, amount: balances[card] }); });
    const have = sources.reduce((s, x) => s + x.amount, 0);
    return { ok: have >= deal.points, have, missing: Math.max(0, deal.points - have), sources };
  }, [user, balances]);

  const hasWallet = Object.keys(balances).length > 0;
  const value = useMemo(() => ({ balances, hasWallet, refresh, save, affordability }), [balances, hasWallet, refresh, save, affordability]);
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export const useWallet = () => useContext(WalletContext);
